import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { Lock, RotateCcw, Sparkles } from 'lucide-react';
import { BirthForm, BirthFormValue } from './BirthForm';
import { ChartReadOnlyTabs } from './ChartReadOnlyTabs';
import { Chart, computeChart, chartFromPositions } from '../engine/chart';
import type { HouseSystem } from '../engine/houses';
import {
  AstrologyPreviewPayload,
  clearAstrologyPreview,
  readAstrologyPreview,
  writeAstrologyPreview,
} from '../lib/previewMigration';

function chartFromPayload(payload: AstrologyPreviewPayload): Chart {
  if (payload.kind === 'birth') {
    const d = payload.data;
    return computeChart({
      name: d.name || 'self',
      year: d.year, month: d.month, day: d.day,
      hour: d.hour, minute: d.minute,
      tzOffset: d.tzOffset, lat: d.lat, lon: d.lon, placeName: d.placeName,
      houseSystem: d.houseSystem ?? 'placidus',
    });
  }
  return chartFromPositions(payload.positions, 'preview', payload.houseSystem ?? 'whole');
}

const SignUpToContinue: React.FC<{ feature: string; description: string }> = ({
  feature,
  description,
}) => (
  <Card className="p-8 bg-card/50 text-center space-y-4">
    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
      <Lock className="w-5 h-5 text-primary" />
    </div>
    <div className="space-y-1">
      <h3 className="font-display text-xl">{feature} is account-only</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">{description}</p>
    </div>
    <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
      <Button asChild>
        <Link to="/auth?signup=1">Create an account to unlock</Link>
      </Button>
      <Button asChild variant="ghost" size="sm">
        <Link to="/auth">Sign in</Link>
      </Button>
    </div>
  </Card>
);

export const AstrologyPreview: React.FC = () => {
  const [payload, setPayload] = useState<AstrologyPreviewPayload | null>(null);

  // Restore preview on mount
  useEffect(() => {
    const stored = readAstrologyPreview();
    if (stored) setPayload(stored);
  }, []);

  const chart = useMemo(() => (payload ? chartFromPayload(payload) : null), [payload]);

  const handleBirth = (v: BirthFormValue) => {
    const next: AstrologyPreviewPayload = {
      kind: 'birth',
      data: {
        name: v.name || undefined,
        year: v.year, month: v.month, day: v.day,
        hour: v.hour, minute: v.minute,
        tzOffset: v.tzOffset, lat: v.lat, lon: v.lon, placeName: v.placeName,
        ianaTz: v.ianaTz,
        houseSystem: v.houseSystem ?? 'placidus',
      },
      savedAt: Date.now(),
    };
    writeAstrologyPreview(next);
    setPayload(next);
  };

  const handleImport = (positions: Record<string, number>, houseSystem: HouseSystem = 'whole') => {
    const next: AstrologyPreviewPayload = {
      kind: 'imported',
      positions,
      houseSystem,
      savedAt: Date.now(),
    };
    writeAstrologyPreview(next);
    setPayload(next);
  };

  const reset = () => {
    clearAstrologyPreview();
    setPayload(null);
  };

  if (!chart || !payload) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center space-y-2">
          <Sparkles className="w-8 h-8 mx-auto text-primary" />
          <h2 className="font-display text-2xl">Cast your natal chart — free preview</h2>
          <p className="text-sm text-muted-foreground">
            Generated entirely in your browser. Nothing is saved until you create an account.
          </p>
        </div>
        <BirthForm onSubmit={handleBirth} onImportPositions={handleImport} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">
            {payload.kind === 'birth' ? (payload.data.name || 'Preview chart') : 'Imported chart'}
          </h2>
          {payload.kind === 'birth' && payload.data.placeName && (
            <p className="text-sm text-muted-foreground">{payload.data.placeName}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Preview only — not saved.{' '}
            <Link to="/auth?signup=1" className="underline hover:text-foreground">
              Create an account
            </Link>{' '}
            to keep it.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reset}>
          <RotateCcw className="w-4 h-4 mr-2" /> Cast another chart
        </Button>
      </div>

      <ChartReadOnlyTabs
        chart={chart}
        extraTriggers={
          <>
            <TabsTrigger value="transits">Transits</TabsTrigger>
            <TabsTrigger value="synastry">Synastry</TabsTrigger>
            <TabsTrigger value="ai">AI</TabsTrigger>
            <TabsTrigger value="export">Export</TabsTrigger>
          </>
        }
        extraContent={
          <>
            <TabsContent value="transits" className="pt-4">
              <SignUpToContinue
                feature="Transit forecast"
                description="See how today's planetary movements interact with your natal chart. Requires an account so we can save your chart."
              />
            </TabsContent>
            <TabsContent value="synastry" className="pt-4">
              <SignUpToContinue
                feature="Synastry"
                description="Compare your chart with another person's. Requires an account to save both charts and search opted-in users."
              />
            </TabsContent>
            <TabsContent value="ai" className="pt-4">
              <SignUpToContinue
                feature="AI readings"
                description="Personalized natal and synastry interpretations from your chart. Available to signed-in users."
              />
            </TabsContent>
            <TabsContent value="export" className="pt-4">
              <SignUpToContinue
                feature="PDF export"
                description="Download a printable PDF of your chart, placements, and aspects. Requires an account."
              />
            </TabsContent>
          </>
        }
      />
    </div>
  );
};